import { useFeatureValue } from '@growthbook/growthbook-react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete';
import { FormFieldWithInfo } from '@openx/components-core/lib/FormFieldWithInfo/FormFieldWithInfo';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField';
import { AgencyAutoComplete } from '@openx/components-deal/lib/components/AgencyAutoComplete/AgencyAutoComplete';
import { BrandAutoComplete } from '@openx/components-deal/lib/components/BrandAutoComplete/BrandAutoComplete';
import { DealParticipants } from '@openx/components-deal/lib/components/DealParticipants/DealParticipants';
import { getDealsLabels } from '@openx/components-deal/lib/DealForm/utils/dealFormUtils';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext';
import { participantFunctionsDSP } from '@openx/components-deal/lib/utils/participantFunctions';
import { ORGANIZATION_UIDS_ALLOWED_TO_SEE_AQUA_DSP } from '@openx/constants/feature-flag-keys';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { type DealFormData, dealFormField, oaDealStatus } from '@openx/types/deal';
import { UserRole } from '@openx/types/user';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { AQUA_RTB_DEMAND_PARTNER_ID } from '@openx/utils/services/config';
import { getDealStatuses4Form } from '@openx/utils/state/deals/resourceType';
import { type JSX, type SyntheticEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/AuthContext/context';
import { isInternalUser } from '../../../permissions/validators';
import { canActivateDealWithAudience } from '../utils/canActivateDealWithAudience';
import { useFieldsVisibility } from './useFieldsVisibility';

type DealDetailsProps = {
  isEdit: boolean;
  onAudienceValidationChange?: (hasError: boolean) => void;
};

export const DealDetails = ({ isEdit, onAudienceValidationChange }: DealDetailsProps): JSX.Element => {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { useIsAllowed } = usePermissionContext();
  const { accessUser } = useIsAllowed();
  const { isOa, useExpectedAdCategoryFetch, useDemandPartnersFetch, useDealSourceFetch, useDealFormSelector } =
    useDealContext();
  const organizationUidsAllowedToSeeAquaDsp = useFeatureValue<string[]>(ORGANIZATION_UIDS_ALLOWED_TO_SEE_AQUA_DSP, []);
  const {
    getValues,
    setValue,
    trigger,
    formState: { isSubmitting, validatingFields },
  } = useFormContext<DealFormData>();

  const initialImpressionGoal = useRef<number>(
    getValues(dealFormField.IMPRESSION_GOAL) ? Number(getValues(dealFormField.IMPRESSION_GOAL)) : 0
  );

  const { expectedAdCategoryMap } = useExpectedAdCategoryFetch();
  const { dealSource, dealSourceOptions } = useDealSourceFetch();
  const { dealForm } = useDealFormSelector();

  const isNameValidating = !!validatingFields?.[dealFormField.NAME];

  const dealsLabels = useMemo(() => getDealsLabels(t), [t]);
  const dealStatusList = useMemo(() => getDealStatuses4Form(t), [t]);

  const audienceValidation = useMemo(() => {
    if (!isEdit || !dealForm.currentDeal || !isOa) {
      return null;
    }
    const currentDeal = dealForm.currentDeal;

    return canActivateDealWithAudience(currentDeal);
  }, [isEdit, dealForm, isOa]);

  const observableStatus = useWatch({ name: dealFormField.STATUS });

  const audienceWarning = useMemo(() => {
    if (!audienceValidation || audienceValidation.allowed) {
      return null;
    }

    // Only show warning when trying to CHANGE status to Active (not when it's already Active)
    const originalStatus = dealForm.currentDeal?.status;

    if (observableStatus === oaDealStatus.ACTIVE && originalStatus !== oaDealStatus.ACTIVE) {
      return audienceValidation.message;
    }

    return null;
  }, [audienceValidation, observableStatus, dealForm.currentDeal?.status]);

  useEffect(() => {
    onAudienceValidationChange?.(!!audienceWarning);
  }, [audienceWarning, onAudienceValidationChange]);

  const observableImpression = useWatch<Pick<DealFormData, 'lifetime_impression_goal'>>({
    name: dealFormField.IMPRESSION_GOAL,
  });

  const observableDealType = useWatch({ name: dealFormField.PMP_DEAL_TYPE });

  const { stateFieldsVisibility } = useFieldsVisibility();

  const expectedAdCategoryOptions = useMemo(() => Object.keys(expectedAdCategoryMap), [expectedAdCategoryMap]);
  const statusOptions = useMemo(() => Object.keys(dealStatusList), [dealStatusList]);
  const StatusTooltip = useMemo(
    () =>
      ({ children }) => {
        if (isEdit) {
          return (
            <FormFieldWithInfo
              info={t(
                'deal_form_status_info_edit',
                "Select 'Paused' if you want deal to be paused. By selecting 'Active', deal will start running."
              )}
            >
              {children}
            </FormFieldWithInfo>
          );
        }

        return children;
      },
    [isEdit, t]
  );

  const showImpressionGoalWarning =
    initialImpressionGoal.current > (observableImpression ? Number(observableImpression) : 0);

  const handleDealSourceChange = useCallback(
    (_: SyntheticEvent, value: string | null) => {
      setValue(dealFormField.DEAL_SOURCE, value);
      trigger(dealFormField.END_DATE);
    },
    [setValue, trigger]
  );

  const isDealSourceVisible = isInternalUser(session) || accessUser?.roles.includes(UserRole.NETWORK_OA_OPENX_BUYER);

  const blockedToSelectPartnerIds = useMemo(() => {
    const isOpenXEmailUser = session?.email?.endsWith('@openx.com');
    const accountId = session?.account_id || session?.account?.id;
    const isOrganizationAllowedToSeeAquaDsp = !!accountId && organizationUidsAllowedToSeeAquaDsp.includes(accountId);

    if (!isOpenXEmailUser && !isOrganizationAllowedToSeeAquaDsp) {
      // Temporary fix to block 'Aqua DSP - RTB' DSP for non OpenX users
      return [AQUA_RTB_DEMAND_PARTNER_ID];
    }

    return undefined;
  }, [session, organizationUidsAllowedToSeeAquaDsp]);

  const expectedAdCategoryInfo = t(
    'deal_form_expected_sensitive_category_info',
    'Select any sensitive categories your deal may include. This disclosure helps publishers enforce their policies and local laws and may limit available inventory; it does not change targeting.'
  );

  return (
    <FormSection divider title={t('Deal Details')} rightColumnSize={RIGHT_COLUMN_WIDTH}>
      <RHFTextField
        name={dealFormField.NAME}
        label={dealsLabels[dealFormField.NAME]}
        placeholder={t('Type your Deal Name')}
        fullWidth
        dataTest="deal-name"
        slotProps={{
          input: {
            disableUnderline: true,
            sx: {
              position: 'relative',
              '& input': { paddingRight: isNameValidating ? '32px' : undefined },
              '& .MuiInputAdornment-root': {
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
              },
            },
            endAdornment: isNameValidating ? (
              <InputAdornment position="end">
                <CircularProgress size={16} data-test="deal-name-validating" aria-label={t('Validating deal name')} />
              </InputAdornment>
            ) : undefined,
          },
          inputLabel: { disableAnimation: true, focused: false, shrink: true },
        }}
      />

      <StatusTooltip>
        <RHFAutoComplete
          name={dealFormField.STATUS}
          textFieldProps={{
            label: dealsLabels[dealFormField.STATUS],
            margin: 'dense',
          }}
          disableClearable
          renderOptions={{
            dataTest: 'deal-status-item',
            getOptionValue: option => dealStatusList[option],
          }}
          getOptionLabel={option => dealStatusList[option] || option}
          options={statusOptions}
          disabled={isOa && !isEdit}
          data-test="deal-status"
        />
      </StatusTooltip>

      {audienceWarning && (
        <Alert severity="warning" data-test="audience-status-warning" sx={{ mb: 2 }}>
          {audienceWarning}
        </Alert>
      )}

      {isDealSourceVisible && (
        <RHFAutoComplete
          name={dealFormField.DEAL_SOURCE}
          onChange={handleDealSourceChange}
          textFieldProps={{
            label: t('{label}', { label: dealsLabels[dealFormField.DEAL_SOURCE] }),
            margin: 'dense',
            placeholder: t('[ Select Deal Source ]'),
          }}
          renderOptions={{
            dataTest: 'deal-source-item',
            getOptionValue: option => dealSource[option]?.name,
          }}
          getOptionLabel={option => dealSource[option]?.name || option}
          options={dealSourceOptions}
          data-test="deal-source"
        />
      )}

      {isInternalUser(session) && (
        <>
          <AgencyAutoComplete />
          <BrandAutoComplete />
        </>
      )}

      <DealParticipants
        isEdit={isEdit}
        dealType={observableDealType}
        participantFunctions={participantFunctionsDSP}
        isSection={false}
        useDemandPartnersFetch={useDemandPartnersFetch}
        blockedToSelectPartnerIds={blockedToSelectPartnerIds}
      />
      {stateFieldsVisibility.showImpression && (
        <>
          <RHFTextField
            name={dealFormField.IMPRESSION_GOAL}
            label={dealsLabels[dealFormField.IMPRESSION_GOAL]}
            placeholder="0"
            fullWidth
            dataTest="impression-goal"
          />
          {showImpressionGoalWarning && (
            <Box sx={{ marginTop: 1.5 }}>
              <Alert severity="warning">
                {t(
                  'If the new impression goal is lower than the number of impressions that was already reported, the deal status will change within couple of minutes.'
                )}
              </Alert>
            </Box>
          )}
        </>
      )}

      <FormFieldWithInfo dataTest="expected-ad-category-info" info={expectedAdCategoryInfo}>
        <RHFAutoComplete
          name={dealFormField.EXPECTED_AD_CATEGORY}
          data-test="expected-ad-category"
          textFieldProps={{
            label: `${dealsLabels[dealFormField.EXPECTED_AD_CATEGORY]} (Optional)`,
            margin: 'dense',
            placeholder: '[ Select Category ]',
          }}
          getOptionLabel={option => expectedAdCategoryMap[option]?.name || option}
          options={expectedAdCategoryOptions}
          disabled={isSubmitting}
        />
      </FormFieldWithInfo>
    </FormSection>
  );
};
